/**
 * Record filtering and grouping helpers for the Contact detail and edit pages.
 * 
 * These pure functions implement the shared Record_Filter_Model (Property 2)
 * ensuring identical filtering behaviour across both views.
 * 
 * Validates: Requirements 2.2, 2.3, 2.4, 2.5, 2.6, 2.7
 */

import type { FinancialRecord, FinancialRecordType } from '@akfdev-com/shared';
import type { RecordFilterModel } from './types';
import { deriveStatus } from './status';

/**
 * Maps RecordFilterModel type values to FinancialRecordType values.
 * Note: 'Payments' maps to 'payment'; other types map directly but lowercase.
 */
function mapFilterTypeToRecordType(
  filterType: RecordFilterModel['type'],
): FinancialRecordType[] {
  if (filterType === 'All') {
    return ['invoice', 'bill', 'payment', 'service_charge'];
  }
  if (filterType === 'Invoices') {
    return ['invoice'];
  }
  if (filterType === 'Bills') {
    return ['bill'];
  }
  if (filterType === 'Service Charges') {
    return ['service_charge'];
  }
  if (filterType === 'Payments') {
    return ['payment'];
  }
  return ['invoice', 'bill', 'payment', 'service_charge'];
}

/**
 * Apply a RecordFilterModel to filter financial records.
 * 
 * Filtering rules:
 * - type !== 'All': keeps only records of the mapped FinancialRecordType
 * - status !== null: keeps only records whose derived status equals it
 * - both set: logical AND (both conditions must match)
 * - never filters by building (Req 2.6)
 * 
 * @param records - Array of financial records to filter
 * @param filter - The RecordFilterModel with type and status filters
 * @param now - Current date for status derivation
 * @returns Filtered array of records
 */
export function applyRecordFilter(
  records: FinancialRecord[],
  filter: RecordFilterModel,
  now: Date,
): FinancialRecord[] {
  return records.filter((record) => {
    // Type filter: if not 'All', check that record type matches
    if (filter.type !== 'All') {
      const allowedTypes = mapFilterTypeToRecordType(filter.type);
      if (!allowedTypes.includes(record.type)) {
        return false;
      }
    }

    // Status filter: if set, derive status and check that it matches
    if (filter.status !== null) {
      const recordStatus = deriveStatus(
        record.amountPaid ?? 0,
        record.amount ?? 0,
        record.dueDate ?? '',
        now,
      );
      if (recordStatus !== filter.status) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Group records by their FinancialRecordType.
 * Within each group, records are ordered by date descending (most recent first).
 * Only non-empty groups are returned (Req 2.2, 2.8).
 * 
 * @param records - Array of financial records to group
 * @returns Record mapping from FinancialRecordType to array of records in that type (only non-empty groups)
 */
export function groupByRecordType(
  records: FinancialRecord[],
): Partial<Record<FinancialRecordType, FinancialRecord[]>> {
  const grouped: Record<FinancialRecordType, FinancialRecord[]> = {
    invoice: [],
    bill: [],
    payment: [],
    service_charge: [],
  };

  // Sort all records by date descending first (to ensure within-group ordering)
  const sortedByDate = [...records].sort((a, b) => {
    const dateA = new Date(a.date ?? '').getTime();
    const dateB = new Date(b.date ?? '').getTime();
    return dateB - dateA; // descending (most recent first)
  });

  // Group into buckets
  for (const record of sortedByDate) {
    const bucket = grouped[record.type];
    if (bucket) {
      bucket.push(record);
    }
  }

  // Return only non-empty groups
  const result: Partial<Record<FinancialRecordType, FinancialRecord[]>> = {};
  for (const [type, items] of Object.entries(grouped)) {
    if (items.length > 0) {
      result[type as FinancialRecordType] = items;
    }
  }

  return result;
}

/**
 * Map a FinancialRecordType to its presentable human-readable label.
 * Used for group headers in FinancialRecordsTable (Req 2.2, 2.8).
 * 
 * @param type - The FinancialRecordType to label
 * @returns Title Case label for the record type
 */
export function recordTypeLabel(type: FinancialRecordType): string {
  const labels: Record<FinancialRecordType, string> = {
    invoice: 'Invoices',
    bill: 'Bills',
    payment: 'Payments',
    service_charge: 'Service Charges',
  };
  return labels[type];
}
